/**
 * 콘솔 목록 표 — 줄 끝 수정 · 줄 클릭 팝업 · 끌어서 순서 바꾸기 (2026-09-09 사용자 지시).
 *
 * ★★ 순서 바꾸기는 «드래그»로 한다 — 사용자 결정(선택지 「나」, 2026-09-09).
 *    정본 axi-components.html 의 **E1-3 「순서가 뜻을 갖는 표」** 는 「끌어서 옮기지
 *    않는다」로 적혀 있고 그 이유를 셋 들었다(①손가락·키보드로 못 한다 ②목록이 길면
 *    스크롤과 싸운다 ③위·아래는 읽어 주기도 된다). 그중 ①을 이 부품이 스스로 푼다 —
 *    **손잡이는 `<button>` 이고 방향키로도 옮긴다**(Alt 없이 ↑·↓). 정본 문안을 고치는 일은
 *    사용자 몫이라 여기서는 코드로만 갈라 둔다.
 *
 * ★ 손잡이는 **따로 선다**. 줄 전체를 잡게 하면 「조금 움직인 클릭」이 팝업을 열어
 *   ⑤「줄을 누르면 수정 팝업」과 서로를 잡아먹는다.
 *
 * ★ 번호는 첫 칸에 **보여 주기만** 한다 — 숫자로 고치는 칸은 팝업에만 둔다(정본 E1-3 유지).
 */
import { useState, type ReactNode } from 'react';
import { GripVertical } from '../../icons';
import './console-list.css';

export interface ConsoleListColumn<T> {
  key: string;
  header: string;
  className?: string;
  render: (row: T) => ReactNode;
}

export interface ConsoleListTableProps<T> {
  rows: T[];
  columns: ConsoleListColumn<T>[];
  rowKey: (row: T) => string | number;
  /** 줄을 누르면 열리는 것 — 대개 수정 팝업. 주지 않으면 줄이 눌리지 않는다. */
  onRowClick?: (row: T) => void;
  /** 줄 끝 작업 열(수정·삭제…). 늘 보여야 하므로 hover 가 아니라 «열»로 선다(정본 E1). */
  rowActions?: (row: T) => ReactNode;
  /**
   * 순서가 뜻인 목록이면 준다 — 번호 열과 손잡이가 서고 끌어서 옮길 수 있다.
   * `onReorder(from, to)` 는 **화면이** 저장까지 맡는다(이 부품은 자리만 바꿔 알린다).
   */
  ordering?: { onReorder: (from: number, to: number) => void; disabled?: boolean };
  empty?: ReactNode;
}

export function ConsoleListTable<T>({
  rows, columns, rowKey, onRowClick, rowActions, ordering, empty,
}: ConsoleListTableProps<T>) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [movedKey, setMovedKey] = useState<string | number | null>(null);

  const canOrder = !!ordering && !ordering.disabled;

  function move(from: number, to: number) {
    if (from === to || to < 0 || to >= rows.length) return;
    setMovedKey(rowKey(rows[from]!));
    ordering!.onReorder(from, to);
  }

  if (rows.length === 0 && empty) {
    return <div className="cl-empty">{empty}</div>;
  }

  return (
    <table className="cl-table">
      <thead>
        <tr>
          {ordering && <th className="cl-table__grip-col" aria-label="순서 바꾸기" />}
          {ordering && <th className="cl-table__ord">#</th>}
          {columns.map((c) => (
            <th key={c.key} className={c.className}>{c.header}</th>
          ))}
          {rowActions && <th className="cl-table__act">작업</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const key = rowKey(row);
          const cls = [
            onRowClick ? 'is-clickable' : '',
            dragFrom === i ? 'is-dragging' : '',
            overIdx === i && dragFrom !== null && dragFrom !== i ? 'is-over' : '',
            movedKey === key ? 'is-moved' : '',
          ].filter(Boolean).join(' ');
          return (
            <tr
              key={key}
              className={cls || undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onDragOver={canOrder ? (e) => { e.preventDefault(); setOverIdx(i); } : undefined}
              onDrop={canOrder ? (e) => {
                e.preventDefault();
                if (dragFrom !== null) move(dragFrom, i);
                setDragFrom(null); setOverIdx(null);
              } : undefined}
            >
              {ordering && (
                <td className="cl-table__grip-col" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="cl-grip"
                    draggable={canOrder}
                    disabled={!canOrder}
                    aria-label={`순서 바꾸기 — ${i + 1}번째. 방향키 위·아래로도 옮길 수 있습니다.`}
                    title="끌어서 순서 변경 (방향키로도 가능)"
                    onDragStart={(e) => {
                      setDragFrom(i);
                      e.dataTransfer.effectAllowed = 'move';
                      try { e.dataTransfer.setData('text/plain', String(i)); } catch { /* jsdom */ }
                    }}
                    onDragEnd={() => { setDragFrom(null); setOverIdx(null); }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp') { e.preventDefault(); move(i, i - 1); }
                      if (e.key === 'ArrowDown') { e.preventDefault(); move(i, i + 1); }
                    }}
                  >
                    <GripVertical size={15} />
                  </button>
                </td>
              )}
              {ordering && <td className="cl-table__ord">{i + 1}</td>}
              {columns.map((c) => (
                <td key={c.key} className={c.className}>{c.render(row)}</td>
              ))}
              {rowActions && (
                <td className="cl-table__act" onClick={(e) => e.stopPropagation()}>
                  <span className="cl-table__acts">{rowActions(row)}</span>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
